import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { usePostAppointmentMutation } from "@/mutations/usePostAppointmentMutation";
import { useDeleteClassesMutation } from "@/mutations/useDeleteClassesMutation";
import { useGetSameDayCancellationsQuery } from "@/queries/useGetSameDayCancellationsQuery";
import { useSameDayCancelActions } from "@/hooks/useSameDayCancelActions";
import { getCookie } from "@/utils/cookies";
import { useClub } from "@/contexts/ClubContext";
import { useAuth } from "@/contexts/AuthContext";
import { PERSONAL_TRAINING_SERVICE_ID } from "@/config/constants";
import {
  Slot,
  Trainer,
  bookingIsAvailable,
  isSameDayCancellationAllowedForPhone,
} from "@/utils/booking";
import { formatDateWithTime } from "@/utils/dateFormatters";
import type { AppointmentData } from "@/http/classesApi";

interface UseSignUpSlotParams {
  slot: Slot;
  trainers: Trainer[];
  appointmentsData?: AppointmentData[];
  userName: string;
  onSuccessfulSignUp: () => void;
}

export const useSignUpSlot = ({
  slot,
  trainers,
  appointmentsData,
  userName,
  onSuccessfulSignUp,
}: UseSignUpSlotParams) => {
  const [open, setOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [sameDayCancelDialog, setSameDayCancelDialog] = useState(false);
  const [deleteSameDayCancelDialog, setDeleteSameDayCancelDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<string | null>(null);
  const [isTimeBooked, setIsTimeBooked] = useState(false);
  const userToken = getCookie("user_token");
  const { selectedClubId, selectedClubName } = useClub();
  const { authPhone } = useAuth();

  const { mutateAsync: postAppointmentMutate } = usePostAppointmentMutation();
  const { mutateAsync: deleteClassesMutate } = useDeleteClassesMutation();
  const { data: sameDayCancellations = [], isLoading: isSameDayCancellationsLoading } =
    useGetSameDayCancellationsQuery();
  const { create: createSameDayCancel, remove: removeSameDayCancel } = useSameDayCancelActions();

  const isBookingAvailable = bookingIsAvailable(slot);

  useEffect(() => {
    if (appointmentsData) {
      setIsTimeBooked(
        appointmentsData.some(
          (appointment) =>
            appointment.start_date === slot.date_time &&
            appointment.employee.id === slot.employee_id &&
            appointment.status !== "canceled"
        )
      );
    }
  }, [appointmentsData, slot.date_time, slot.employee_id]);

  const handleSignUp = () => {
    const trainerName = trainers.find((t) => t.id === slot.employee_id)?.name || "Не указан";

    postAppointmentMutate({
      date_time: slot.date_time,
      service_id: PERSONAL_TRAINING_SERVICE_ID,
      employee_id: slot.employee_id,
      room_id: slot.room_id,
      club_id: selectedClubId,
      user_token: userToken,
    })
      .then((response) => {
        setOpen(false);
        setIsTimeBooked(true);
        const bookedDate = response.data?.appointment?.date_time || slot.date_time;
        toast.success("Запись подтверждена", {
          description: `${formatDateWithTime(bookedDate)} • АкваСтар ${selectedClubName} • ${trainerName}`,
        });
        if (onSuccessfulSignUp) {
          onSuccessfulSignUp();
        }
      })
      .catch((err) => {
        let message = "Произошла ошибка при записи на занятие";
        if (err.response) {
          switch (err.response.data.error) {
            case 1034:
              message =
                "Вы расписали все доступные занятия, либо абонемент заканчивается раньше указанной даты.";
              break;
            default:
              message = err.response.data.error_message;
          }
        }
        toast.error("Ошибка", { description: message });
        if (onSuccessfulSignUp) {
          onSuccessfulSignUp();
        }
      });
  };

  const handleCancelClass = () => {
    if (appointmentsData) {
      const appointment = appointmentsData.find(
        (app) => app.start_date === slot.date_time && app.employee.id === slot.employee_id
      );

      if (appointment) {
        setSelectedAppointment(appointment.appointment_id);
        setDeleteDialog(true);
      }
    }
  };

  const handleSameDayCancel = () => {
    if (appointmentsData) {
      const appointment = appointmentsData.find(
        (app) => app.start_date === slot.date_time && app.employee.id === slot.employee_id
      );

      if (appointment) {
        setSelectedAppointment(appointment.appointment_id);
        setSameDayCancelDialog(true);
      }
    }
  };

  const handleConfirmSameDayCancel = async () => {
    if (!authPhone || !selectedAppointment) {
      toast.error("Не удалось определить номер телефона");
      return;
    }

    const appointment = appointmentsData?.find((app) => app.appointment_id === selectedAppointment);

    await createSameDayCancel({
      phone: authPhone,
      clubId: selectedClubId,
      exerciseId: selectedAppointment,
      exerciseTitle: appointment?.service?.title || "",
      exerciseTime: appointment?.start_date || slot.date_time,
      toastDescription: `${formatDateWithTime(slot.date_time)} • АкваСтар ${selectedClubName}`,
    });
    setSameDayCancelDialog(false);
  };

  const handleDeleteSameDayCancel = () => {
    setDeleteSameDayCancelDialog(true);
  };

  const handleConfirmDeleteSameDayCancel = async () => {
    if (!authPhone || !appointmentsData) return;
    const appointment = appointmentsData.find(
      (app) => app.start_date === slot.date_time && app.employee.id === slot.employee_id
    );
    if (!appointment) return;
    const cancellation = sameDayCancellations.find(
      (c) => c.exercise_id === appointment.appointment_id
    );
    if (!cancellation) return;

    await removeSameDayCancel({ phone: authPhone, cancellationId: cancellation.id });
    setDeleteSameDayCancelDialog(false);
  };

  const hasSameDayCancellation = useMemo(() => {
    if (!isSameDayCancellationAllowedForPhone(authPhone)) return null;
    if (isSameDayCancellationsLoading || !appointmentsData) return null;
    const appointment = appointmentsData.find(
      (app) =>
        app.start_date === slot.date_time &&
        app.employee.id === slot.employee_id &&
        app.status !== "canceled"
    );
    if (!appointment) return false;
    return sameDayCancellations.some((c) => c.exercise_id === appointment.appointment_id);
  }, [
    appointmentsData,
    sameDayCancellations,
    isSameDayCancellationsLoading,
    slot.date_time,
    slot.employee_id,
  ]);

  const handleConfirmDelete = () => {
    const trainerName = trainers.find((t) => t.id === slot.employee_id)?.name || "Не указан";

    deleteClassesMutate({
      usertoken: userToken,
      appointment_id: selectedAppointment,
    })
      .then(() => {
        setDeleteDialog(false);
        setIsTimeBooked(false);
        toast.success("Занятие успешно отменено", {
          description: `${formatDateWithTime(slot.date_time)} • АкваСтар ${selectedClubName} • ${trainerName}`,
        });
        if (onSuccessfulSignUp) {
          onSuccessfulSignUp();
        }
      })
      .catch((err) => {
        let message = "При отмене занятия произошла ошибка. Обратитесь к администратору.";
        if (err.response?.data?.error_message) {
          message = err.response.data.error_message;
        }
        setDeleteDialog(false);
        toast.error("Ошибка", { description: message });
      });
  };

  const trainerListActiveState = useMemo(() => {
    return trainers.filter((trainer) => trainer.id === slot.employee_id);
  }, [trainers, slot.employee_id]);

  // Get trainer name from appointment data (for bookings from other clubs)
  const bookedTrainerName = useMemo(() => {
    if (!appointmentsData) return null;
    const appointment = appointmentsData.find(
      (app) =>
        app.start_date === slot.date_time &&
        app.employee.id === slot.employee_id &&
        app.status !== "canceled"
    );
    return appointment?.employee?.name || null;
  }, [appointmentsData, slot.date_time, slot.employee_id]);

  useEffect(() => {
    if (slot.appointment_id) {
      setIsTimeBooked(true);
    } else {
      setIsTimeBooked(false);
    }
  }, [slot.appointment_id]);

  return {
    open,
    deleteDialog,
    sameDayCancelDialog,
    isTimeBooked,
    handleSignUp,
    handleCancelClass,
    handleConfirmDelete,
    handleSameDayCancel,
    handleConfirmSameDayCancel,
    handleDeleteSameDayCancel,
    handleConfirmDeleteSameDayCancel,
    deleteSameDayCancelDialog,
    setDeleteSameDayCancelDialog,
    hasSameDayCancellation,
    trainerListActiveState,
    bookedTrainerName,
    userName,
    setOpen,
    setDeleteDialog,
    setSameDayCancelDialog,
    isBookingAvailable,
  };
};
